/**
 * g.fetch - talk to the app's API, to our own PHP, or to an external URL.
 *
 *   const res = await g.fetch(endpoint, data, opts);
 *   // res = the API's JSON envelope: { success, error?, data?, pagination? }
 *   // - never throws; when the request itself fails, g.fetch synthesizes the
 *   // same envelope shape (see CLIENT ERROR CODES below)
 *
 * Endpoint forms:
 *   '/path/to/endpoint'         -> settings.api + path, X-Api-Token header sent
 *   'native:/path/to/endpoint'  -> current origin + path (PHP wrappers - e.g.
 *                                  login/logout, which must set PHP cookies);
 *                                  no X-Api-Token
 *   'https://...' (full URL)    -> external API as-is; no X-Api-Token
 *
 * Cookies are ALWAYS sent (credentials: 'include') - the PHP session cookies
 * are the user's identity. Requires the API to answer CORS with the exact
 * origin (not *), Access-Control-Allow-Credentials: true and X-Api-Token in
 * Access-Control-Allow-Headers.
 *
 * The body always has the shape { data, pagination } so the API reads
 * req.body.data / req.body.pagination:
 *   - plain data                -> JSON request
 *   - data containing File/Blob/FileList (any depth) -> multipart FormData:
 *     files are appended under their key path (e.g. 'avatar', 'docs.0'),
 *     'data' and 'pagination' become JSON-string fields (the server shim
 *     JSON.parses them - see docs/libs/genus.md)
 *   - data instanceof FormData  -> sent as-is (escape hatch); only
 *     opts.pagination is appended, nothing else is touched
 *
 * opts.signal: an AbortSignal - aborting cancels the request (error.code 4).
 * opts.progress: false - keep this call off the top progress bar (chatty
 * background loads: selekt lists, validator server checks).
 * opts.keepalive: true - the request survives page unload (browser
 * keepalive semantics; body capped ~64KB by the browser). THE way to send
 * a final flush from pagehide/visibilitychange - full transport contract
 * included (token, tenant lids, language), which sendBeacon cannot carry.
 * Pair it with progress: false (nobody is left to watch the bar).
 * opts.latest: 'key' - LATEST WINS: a newer call with the same key
 * supersedes this one; the stale call resolves { success: false,
 * superseded: true, error: { code: 5 } } (its request is aborted when
 * still in flight) - reads that follow a filter/search/tab.
 * opts.queue: 'key' - SERIAL: calls with the same key run one after the
 * other in call order (order-sensitive writes: reorder up/down).
 * opts.unguarded: true - the call carries its own guard or is safely
 * repeatable: the dev-mode detector (g.action) does not flag it when a
 * click starts it without g.action.
 *
 * ERROR CODES: error.code 0 means "no error". Codes generated HERE (client
 * side, reserved - the API's own codes come in the response body):
 *   1 = invalid endpoint (nothing was sent)
 *   2 = API not responding (network/CORS failure)
 *   3 = invalid API response (body is not valid JSON)
 *   4 = request aborted (opts.signal fired - the caller cancelled it)
 *   5 = superseded (opts.latest: a newer call with the same key ran)
 * The API can also answer success: true WITH error.code > 0 - an expected
 * business error (e.g. row not found). success: false = system error.
 *
 * Full reference: docs/libs/genus.md.
 */
import { settings } from './core.js';
import { route } from './route.js';
import { translate } from './translate.js';
import { progressStart, progressEnd } from './progress.js';
import { checkGuard } from './action.js';

const isPlainObject = (v) => v && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Blob) && !(v instanceof FormData);

const failure = (code, message) => ({ success: false, error: { code, message } });

// Where does this endpoint go, and does it get the API token?
export function resolveEndpoint(endpoint) {
	if (endpoint.startsWith('native:')) {
		// native = our own PHP: carry the page's context prefix (and language)
		// so the per-tab tenant reaches the PHP side too (tenancy.md) — the
		// router strips both before matching, every route answers either way
		let path = endpoint.slice('native:'.length);
		try { path = route.url(path); } catch { /* no routing payload on this page — send as-is */ }
		return { url: window.location.origin + path, withToken: false, tenant: false };
	}
	if (/^https?:\/\//.test(endpoint)) return { url: endpoint, withToken: false, tenant: false };
	if (endpoint.startsWith('/')) return { url: String(settings.api || '').replace(/\/+$/, '') + endpoint, withToken: true, tenant: true };
	return null;
}

// Deep-copy `value` with every File/Blob/FileList pulled out into `files`
// (as { key: 'path.to.field', file }). Removed spots become absent (objects)
// or null (arrays, to keep indices stable).
function stripFiles(value, path, files) {
	if (typeof FileList !== 'undefined' && value instanceof FileList) value = Array.from(value);
	if (value instanceof Blob) {
		files.push({ key: path || 'file', file: value });
		return undefined;
	}
	if (Array.isArray(value)) {
		return value.map((v, i) => {
			const r = stripFiles(v, `${path}.${i}`, files);
			return r === undefined ? null : r;
		});
	}
	if (isPlainObject(value)) {
		const copy = {};
		for (const k of Object.keys(value)) {
			const r = stripFiles(value[k], path ? `${path}.${k}` : k, files);
			if (r !== undefined) copy[k] = r;
		}
		return copy;
	}
	return value;
}

// The standard API headers for a resolved target — token, language and the
// page's ACTIVE tenant (lids from the routing payload; the API decodes and
// validates them against the caller's memberships — tenancy.md). Shared by
// g.fetch and g.pdf so a binary download carries exactly the same identity.
export function standardHeaders(target) {
	const headers = {};
	if (target.withToken && settings.apiToken) headers['X-Api-Token'] = settings.apiToken;
	if (target.withToken) {
		// the page's language rides every API call (i18n.md): business
		// errors / validation messages come back in the user's language
		const lang = (window.g && window.g.lang) || settings.lang;
		if (lang) headers['X-Genus-Lang'] = lang;
	}
	if (target.tenant) {
		const company = route.context('company');
		const space = route.context('space');
		if (company) headers['X-Genus-Company'] = company;
		if (space) headers['X-Genus-Space'] = space;
	}
	return headers;
}

const latestCalls = new Map(); // opts.latest key -> the AbortController of the newest call
const queues = new Map();      // opts.queue key -> the tail promise

const superseded = () => ({ success: false, superseded: true, error: { code: 5, message: translate('Superseded') } });

export function apiFetch(endpoint, data, opts = {}) {
	checkGuard('fetch', String(endpoint || ''), opts);
	if (opts.queue) {
		const key = opts.queue;
		const tail = queues.get(key) || Promise.resolve();
		const run = tail.then(() => tracked(endpoint, data, opts));
		queues.set(key, run);
		run.then(() => { if (queues.get(key) === run) queues.delete(key); });
		return run;
	}
	if (opts.latest) return latest(endpoint, data, opts);
	return tracked(endpoint, data, opts);
}

async function latest(endpoint, data, opts) {
	const key = opts.latest;
	const prev = latestCalls.get(key);
	if (prev) prev.abort();
	const ctrl = new AbortController();
	latestCalls.set(key, ctrl);
	if (opts.signal) opts.signal.addEventListener('abort', () => ctrl.abort(), { once: true });
	const res = await tracked(endpoint, data, { ...opts, signal: ctrl.signal });
	if (latestCalls.get(key) !== ctrl) return superseded(); // a newer call ran (aborted or landed late)
	latestCalls.delete(key);
	return res;
}

async function tracked(endpoint, data, opts) {
	if (opts.progress === false) return doFetch(endpoint, data, opts);
	progressStart();
	try {
		return await doFetch(endpoint, data, opts);
	} finally {
		progressEnd();
	}
}

async function doFetch(endpoint, data, opts) {
	const target = resolveEndpoint(String(endpoint || ''));
	if (!target) return failure(1, translate('Invalid endpoint: {endpoint}', { endpoint }));

	const method = (opts.method || 'POST').toUpperCase();
	const headers = standardHeaders(target);

	let body;
	if (method === 'GET' || method === 'HEAD') {
		// These can't carry a body - data/pagination are ignored (use POST).
		body = undefined;
	} else if (data instanceof FormData) {
		body = data;
		if (opts.pagination) body.append('pagination', JSON.stringify(opts.pagination));
	} else {
		const files = [];
		const clean = data === undefined ? null : stripFiles(data, '', files);
		if (files.length) {
			body = new FormData();
			body.append('data', JSON.stringify(clean));
			if (opts.pagination) body.append('pagination', JSON.stringify(opts.pagination));
			files.forEach((f) => body.append(f.key, f.file, f.file.name || undefined));
		} else {
			headers['Content-Type'] = 'application/json';
			const payload = { data: clean };
			if (opts.pagination) payload.pagination = opts.pagination;
			body = JSON.stringify(payload);
		}
	}

	Object.assign(headers, opts.headers || {});

	let res;
	try {
		res = await window.fetch(target.url, { method, credentials: 'include', headers, body, signal: opts.signal, keepalive: opts.keepalive === true });
	} catch (err) {
		if (err && err.name === 'AbortError') return failure(4, translate('Request aborted'));
		// Network failure, CORS rejection...
		return failure(2, translate('API not responding'));
	}

	// The API always answers with the JSON envelope { success, error?, data?,
	// pagination? } (regardless of the HTTP status), so we hand the body over
	// as-is. External APIs return whatever JSON they produce.
	let text;
	try {
		text = await res.text(); // the abort can also hit mid-body
	} catch (err) {
		return err && err.name === 'AbortError' ? failure(4, translate('Request aborted')) : failure(2, translate('API not responding'));
	}
	let envelope;
	try {
		envelope = JSON.parse(text);
	} catch {
		return failure(3, translate('Invalid API response'));
	}
	if (target.withToken) machineMoved(envelope);
	return envelope;
}

// ONE ACTIVE MACHINE PER USER (api.md "One active machine per user"): a
// palette page rendered on the ACTIVE machine whose call now answers 29
// (another computer took over — the session-moved push reaches only a page
// holding a socket) reloads ONCE, so the server renders the 423 question.
// Only a page that rendered active (g.settings.device.active === true):
// the 423 page itself renders inactive, so this can never loop.
let movedReload = false;
function machineMoved(envelope) {
	if (movedReload || envelope?.error?.code !== 29 || settings.device?.active !== true) return;
	movedReload = true;
	window.location.reload();
}
